import { GLOBAL_SEARCH_ENTITY_ICON as ICON } from 'components/global-search/entityDisplay';
import { SavingsOutlined, SchoolOutlined, SquareCheckIcon, type SvgIconComponent } from 'components/icons';
import type { DraftType } from 'types/drafts';
import { CREATE_RECORD_FAMILY, familyTileClass } from 'utils/families';

/** The record types that have a create form, one per create dialog title. */
export type CreateRecordType =
    | 'case'
    | 'contact'
    | 'timecard'
    | 'expense'
    | 'contract'
    | 'client-asset'
    | 'correspondence'
    | 'debriefing'
    | 'experience-record'
    | 'task'
    | 'bd-effort'
    | 'project'
    | 'training'
    | 'marketing-profile'
    | 'employee'
    | 'group'
    | 'team'
    | 'office'
    | 'practice-area'
    | 'job-role'
    | 'job-position';

type CreateRecord = {
    /** "New {record}", the label its list's New button already carries. */
    titleId: string;
    /** The glyph search draws the record with. */
    icon: SvgIconComponent;
};

export const CREATE_RECORDS: Record<CreateRecordType, CreateRecord> = {
    case: { titleId: 'matters.new-case', icon: ICON.MATTER },
    contact: { titleId: 'new-entity.contact', icon: ICON.PARTY },
    timecard: { titleId: 'new-entity.timecard', icon: ICON.TIMECARD },
    expense: { titleId: 'new-entity.expense', icon: ICON.EXPENSE },
    contract: { titleId: 'new-entity.contract', icon: ICON.CONTRACT },
    'client-asset': { titleId: 'client-assets.new-title', icon: SavingsOutlined },
    correspondence: { titleId: 'new-entity.correspondence', icon: ICON.CORRESPONDENCE },
    debriefing: { titleId: 'debriefing.form.new-title', icon: ICON.DEBRIEFING },
    'experience-record': { titleId: 'new-entity.experience-record', icon: ICON.EXPERIENCE_RECORD },
    task: { titleId: 'new-entity.task', icon: SquareCheckIcon },
    'bd-effort': { titleId: 'new-entity.bd-effort', icon: ICON.EFFORT },
    project: { titleId: 'new-entity.project', icon: ICON.PROJECT },
    training: { titleId: 'new-entity.training', icon: SchoolOutlined },
    'marketing-profile': { titleId: 'new-entity.marketing-profile', icon: ICON.PARTY },
    employee: { titleId: 'new-entity.employee', icon: ICON.EMPLOYEE },
    group: { titleId: 'new-entity.group', icon: ICON.PARTY_GROUP },
    team: { titleId: 'new-entity.team', icon: ICON.TEAM },
    office: { titleId: 'new-entity.office', icon: ICON.OFFICE },
    'practice-area': { titleId: 'new-entity.practice-area', icon: ICON.PRACTICE_AREA },
    'job-role': { titleId: 'new-entity.job-role', icon: ICON.JOB_ROLE },
    'job-position': { titleId: 'new-entity.job-position', icon: ICON.JOB_POSITION },
};

/**
 * A record as the dialog's header, its tab, a Saved for later row and a draft's Inbox row draw it: on its family's
 * tile, or the neutral tile for a record of none.
 */
export function createRecord(type: CreateRecordType): CreateRecord & { tileClass: string } {
    return { ...CREATE_RECORDS[type], tileClass: familyTileClass(CREATE_RECORD_FAMILY[type]) };
}

/** The record types that can be saved as a draft, by the path of the record page a draft of each opens on. */
const DRAFT_PAGES: Record<DraftType, string> = {
    case: '/matter',
    contact: '/contact',
    expense: '/expense',
    contract: '/contract',
    'client-asset': '/client-asset',
    debriefing: '/debriefing',
    task: '/task',
    'bd-effort': '/effort',
    project: '/projects',
    training: '/trainings',
    'marketing-profile': '/contacts/marketing',
    employee: '/employee',
    group: '/group',
    team: '/team',
    office: '/office',
    'practice-area': '/practice-area',
    'job-role': '/job-role',
    'job-position': '/job-position',
};

export function isDraftType(type: CreateRecordType): type is DraftType {
    return Object.hasOwn(DRAFT_PAGES, type);
}

/** The record page of a draft, where it is read, shared and deleted. */
export function draftPageOf(type: DraftType, id: number): string {
    return `${DRAFT_PAGES[type]}/${id}`;
}
